export const formatTokenBalance = (
  amount: bigint,
  decimals: number,
  fmtDecimals: number
): string => {
  // amount is in smallest units (integer)
  // Convert to a floating number: amount / 10^decimals
  const divisor = 10 ** decimals;
  const floatValue = Number(amount) / divisor;
  return floatValue.toFixed(fmtDecimals);
};

export function parseTokenAmount(input: string, decimals: number): bigint {
  // Split input into whole and fractional parts
  const parts = input.split('.');
  let whole = parts[0] || '0';
  let fraction = parts[1] || '';

  // Truncate or pad fractional part to match the number of decimals
  if (fraction.length > decimals) {
    fraction = fraction.slice(0, decimals);
  }
  while (fraction.length < decimals) {
    fraction += '0';
  }

  // If input is something like '.' (no whole part), ensure whole is '0'
  if (!whole) whole = '0';

  // Combine and convert to BigInt
  const str = whole + fraction;
  // Handle empty or invalid input gracefully
  if (str.match(/^\d+$/)) {
    return BigInt(str);
  }
  return BigInt(0);
}

/**
 * Formats a large pending reward value into a human-readable decimal string.
 *
 * @param pendingRewards - The raw pending rewards value as a bigint, including scaling factor.
 * @param tokenDecimals - Number of decimals for the token (e.g., 9 for a 9-decimal SPL token).
 * @param scalingFactor - The global scaling factor applied to global_reward_per_token (e.g., 1_000_000_000_000n).
 * @param displayDecimals - The number of decimal places to show to the user.
 * @returns A string representing the formatted reward amount, e.g. "1234.56789"
 */
export function formatPendingRewards(
  pendingRewards: bigint,
  tokenDecimals: number,
  scalingFactor: bigint,
  displayDecimals: number
): string {
  // 1. Convert from scaled units to smallest token units
  const smallestUnits = pendingRewards / scalingFactor; // floor division

  // Convert smallestUnits to string for decimal formatting
  let s = smallestUnits.toString();

  // If the string is shorter than tokenDecimals, pad with leading zeros
  if (s.length <= tokenDecimals) {
    s = s.padStart(tokenDecimals + 1, '0');
    // Now s has at least (tokenDecimals+1) digits, so we can safely insert a decimal
  }

  // Split the string into whole and fractional parts
  const whole = s.slice(0, s.length - tokenDecimals);
  let fraction = s.slice(s.length - tokenDecimals);

  // Truncate fraction if needed
  if (fraction.length > displayDecimals) {
    fraction = fraction.slice(0, displayDecimals);
  }

  // If displayDecimals is 0, just return the whole part
  if (displayDecimals === 0) {
    return whole;
  }

  return whole + '.' + fraction;
}

export function parseSmallestAmount(input: string, decimals: number): bigint {
  const parts = input.split('.');
  let whole = parts[0] || '0';
  let fraction = parts[1] || '';

  if (fraction.length > decimals) {
    fraction = fraction.slice(0, decimals);
  }
  while (fraction.length < decimals) {
    fraction += '0';
  }

  const str = whole + fraction;
  if (!/^\d+$/.test(str)) {
    return BigInt(0);
  }
  return BigInt(str);
}
